class LoggerTimeSeries {
  constructor(config) {
    this.container = config.container;
    this.data = config.data || [];
    this.xAxis = config.xAxis || {};
    this.yAxis = config.yAxis || {};
    this.events = config.events || {};
    this.aggregationLevel = config.aggregationLevel;
    this.heightScale = config.heightScale || 0.2;
    this.widthScale = config.widthScale || 0.98;
    this.create15MinDisplay = config.create15MinDisplay;
    this.color = index => d3.schemeSet1[index];
    this.parameterId = config.parameterId || 0;
  }

  /**
   * This function is to check if the user has provided values
   * for a required config property
   * @param {*} prop
   */
  checkConfigProp(prop) {
    if (typeof prop.value === 'undefined') {
      throw `Error: Please provide a value for the loggerTimeSeries "${prop.key}" config option `;
    }
  }

  /**
   * This function is used to determine if the config
   * object is properly configured for creating the graph
   */
  validateConfig() {
    // required config options
    const configProps = [
      { key: 'container', value: this.container },
      { key: 'data', value: this.data },
      { key: 'aggregationLevel', value: this.aggregationLevel },
    ];
    configProps.forEach(prop => this.checkConfigProp(prop));

    return this;
  }

  /**
   * This method is used to set data for the Heatmap
   * @param {array} data [data for heatmap]
   */
  setData(data) {
    this.data = data;
    return this;
  }

  /**
   * This method is used to set the chart dimensions
   * @param {string} selector  [CSS selector for chart]
   * @param {object} [margin] [margins to apply]
   * @return {object} [chart dimensions]
   */
  setChartDimensions(selector = this.container, margin = { top: 15, right: 15, bottom: 25, left: 50 }) {
    let width = d3.select(selector).style('width');
    width = parseInt(width.substring(0, width.length - 2)) * this.widthScale;

    let height = width * this.heightScale;

    // Subtract margins from width and height
    (width = width - margin.left - margin.right), (height = height - margin.top - margin.bottom);

    const chartDimensions = {
      margin: margin,
      width: width,
      height: height,
    };
    this.chartDimensions = chartDimensions;
    return chartDimensions;
  }

  /**
   * This method is used to generate the tooltip HTML
   * @param {*} data
   */
  setTooltipHTML(data) {
    if (data.length > 0) {
      let html = `<div class="ts-tooltip-title">${data[0].prettyDate}</div>`;
      data.forEach(d => {
        html += `
          <div class="ts-tooltip-value-wrapper">
            <div class="ts-tooltip-legend" style="background-color: ${d.color}"></div>
            <div class="ts-tooltip-value">${d.location_name}: ${d.rvalue} ${d.unit_name}</div>
          </div>`;
      });
      return html;
    } else {
      const existingHTML = document.querySelector(`.tooltip-card-${this.container.replace('#', '')}`).innerHTML;
      return existingHTML;
    }
  }

  /**
   * This function is used to create the graph tooltip
   * @param {*} self
   * @param {*} scale
   * @param {*} tooltipBar
   * @param {*} tooltipLine
   * @param {*} tooltipCard
   */
  createTooltip(self, scale, tooltipBar, tooltipLine, tooltipCard) {
    // grab the location of the users cursor and convert it to a meaningful x-axis value
    const x0 = parseInt(d3.mouse(self)[0]);
    const { width, height, margin } = this.chartDimensions;
    const { aggregationLevel, data } = this;

    let activeData = [];

    // let activeData;
    // let sidebarTitle;
    // let gageAbbrev;

    const date = scale.invert(d3.mouse(self)[0]);

    if (aggregationLevel === 'daily') {
      const formatDate = d3.timeFormat('%Y-%m-%d');
      const prettyDate = d3.timeFormat('%b %d, %Y');
      const formattedDate = formatDate(date);
      activeData = data.filter(d => d.rdate === formattedDate);
      activeData = activeData.map(d => {
        let obj = Object.assign({}, d);
        obj.prettyDate = prettyDate(new Date(`${d.rdate} 00:00:00`));
        if (obj.location_name === 'CT-2') {
          obj.color = this.color(0);
        } else if (obj.location_name === 'CC-10') {
          obj.color = this.color(1);
        } else if (obj.location_name === 'CC-7') {
          obj.color = this.color(2);
        } else if (obj.location_name === 'CT-1') {
          obj.color = this.color(3);
        } else if (obj.location_name === 'CT-P1') {
          obj.color = this.color(4);
        } else if (obj.location_name === 'CT-P2') {
          obj.color = this.color(5);
        } else if (obj.location_name === 'PC-1') {
          obj.color = this.color(6);
        } else if (obj.location_name === 'MHFD Cherry Ck') {
          obj.color = this.color(7);
        } else if (obj.location_name === 'NOAA KAPA') {
          obj.color = this.color(8);
        } else if (obj.location_name === 'NOAA KAPA Max' || obj.location_name === 'NOAA KAPA Min') {
          obj.color = this.color(8);
        } else if (obj.location_name === 'MHFD Cherry Ck Max' || obj.location_name === 'MHFD Cherry Ck Min') {
          obj.color = this.color(7);
        }

        return obj;
      });
    } else {
      const formatDate = d3.timeFormat('%Y-%m-%d %H:%M:00');
      const prettyDate = d3.timeFormat('%b %d, %Y @ %I:%M %p');
      const formattedDate = formatDate(date);

      activeData = data
        .filter(d => d.rdate === formattedDate)
        .map(d => {
          let obj = Object.assign({}, d);
          obj.prettyDate = prettyDate(new Date(obj.rdate));
          if (obj.location_name === 'CT-2') {
            obj.color = this.color(0);
          } else if (obj.location_name === 'CC-10') {
            obj.color = this.color(1);
          } else if (obj.location_name === 'CC-7') {
            obj.color = this.color(2);
          } else if (obj.location_name === 'CT-1') {
            obj.color = this.color(3);
          } else if (obj.location_name === 'CT-P1') {
            obj.color = this.color(4);
          } else if (obj.location_name === 'CT-P2') {
            obj.color = this.color(5);
          } else if (obj.location_name === 'PC-1') {
            obj.color = this.color(6);
          } else if (obj.location_name === 'MHFD Cherry Ck') {
            obj.color = this.color(7);
          } else if (obj.location_name === 'NOAA KAPA') {
            obj.color = this.color(8);
          } else if (obj.location_name === 'NOAA KAPA Max' || obj.location_name === 'NOAA KAPA Min') {
            obj.color = this.color(8);
          } else if (obj.location_name === 'MHFD Cherry Ck Max' || obj.location_name === 'MHFD Cherry Ck Min') {
            obj.color = this.color(7);
          }

          return obj;
        });
    }

    // The tooltip bar is appended to the graph when it is rendered
    // This sets the coordinates of the bar as well as the display styles
    tooltipBar
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '36px')
      .style('opacity', 0.1);

    // The tooltip line is appended to the graph when it is rendered
    // This sets the coordinates of the line as well as the display styles
    tooltipLine
      .attr('x1', x0)
      .attr('x2', x0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', '#5d86ff')
      .style('stroke-width', '1px')
      .style('opacity', 0.8);

    if (activeData.length > 0) {
      tooltipCard
        .style('left', d3.event.pageX + 'px')
        .style('top', d3.event.pageY - 28 + 'px')
        .style('opacity', 1)
        .html(this.setTooltipHTML(activeData));
    }
  }

  /**
   * This method is used to create the statistics table
   * that accompanies the graphs
   * @param {*} self
   * @param {*} scale
   */
  createTable(self, scale) {
    // grab the location of the users cursor and convert it to a meaningful x-axis value
    const x0 = parseInt(d3.mouse(self)[0]);
    const { width, height, margin } = this.chartDimensions;
    const { aggregationLevel, data } = this;

    let activeData = [];

    // let activeData;
    // let sidebarTitle;
    // let gageAbbrev;

    const date = scale.invert(d3.mouse(self)[0]);
    const formatDate = d3.timeFormat('%Y-%m-%d');
    const prettyDate = d3.timeFormat('%b %d, %Y');
    const formattedDate = formatDate(date);
    activeData = data.filter(d => d.rdate === formattedDate);

    activeData = activeData.map(d => {
      let obj = Object.assign({}, d);
      obj.prettyDate = prettyDate(new Date(`${d.rdate} 00:00:00`));
      return obj;
    });

    const statsTable = document.querySelector('#daily-avg-stats tbody');

    const statsTableHTML = activeData.map(d => {
      return `<tr><td>${d.location_name}</td><td>${
        ['1906', '1908'].includes(this.parameterId) ? '--' : `${d.rvalue} ${d.unit_name}`
      }</td><td>${d.max_value} ${d.unit_name}</td><td>${d.min_value} ${d.unit_name}</td>`;
    });
    statsTable.innerHTML = statsTableHTML.join('');
  }

  /**
   * this method is used to clear the stats table
   * @param {*} msg
   */
  removeTable(msg = '<tr><td class="text-center" colspan="4">Please select a value</td></tr>') {
    const statsTable = document.querySelector('#daily-avg-stats tbody');
    if (statsTable !== null) {
      statsTable.innerHTML = msg;
    }
  }

  /**
   * This method is used to remove an existing Heatmap tooltip
   * @return {object} [returns Heatmap]
   */
  removeTooltip(tooltipCard) {
    tooltipCard.style('opacity', 0);
    return this;
  }

  /**
   * This method is used to remove an existing Heatmap
   * @return {object} [returns Heatmap]
   */
  removeSVG(message = '') {
    document.querySelector(this.container).innerHTML = message;
    return this;
  }

  /**
   * This method is used to create the SVG that all of the other chart elements will be appended to
   * @return {object} [returns Heatmap]
   */
  createSVG() {
    const chartDimensions = this.setChartDimensions();
    const { width, height, margin } = chartDimensions;

    this.removeSVG();
    // this.removeTooltip();

    const svg = d3
      .select(this.container)
      .append('svg')
      .attr('width', chartDimensions.width + margin.left + margin.right)
      .attr('height', height + 20)
      .attr('id', 'd3-graph')
      .style('overflow', 'visible')
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    this.svg = svg;
    return this;
  } // createSVG

  /**
   * This method is used to calculate the min/max values for the upstream to downstream dataset
   * The method collapses the values across all value fields into an array for each record
   * That array is then flattened into a single array that the d3.extent method is then run on
   * @param  {array} data [data to evaluate]
   * @return {array}      [returns an array of the min and max]
   */
  calcExtent(data, field) {
    let min = d3.min(data, d => d[field]);
    let max = d3.max(data, d => d[field]);
    // if ( min > 0 ) min = 0;
    return [min, max];
  }

  /**
   * This method is used to create the graph scales
   */
  createScales() {
    const { width, height } = this.chartDimensions;
    const data = this.data;

    // Set X and Y ranges
    const x = d3.scaleTime().range([5, width - 5]);
    const y = d3.scaleLinear().range([height, 0]);
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    // Set X and Y domains aka the min/max x and y axis values
    if (xAxisFormat.toLowerCase() === 'timestamp') {
      data.sort((a, b) => {
        if (a.rdate > b.rdate) return 1;
        if (a.rdate < b.rdate) return -1;
        return 0;
      });
      x.domain(d3.extent(data, d => new Date(d.rdate)));
    } else {
      x.domain(d3.extent(data, d => new Date(`${d.rdate} 00:00:00`)));
    }

    if (data[0].parameterid === 2) {
      y.domain([0, 130]);
    } else if (data[0].parameterid === 1901) {
      y.domain([0, 5]);
    } else {
      y.domain(this.calcExtent(data, 'rvalue'));
    }

    this.x = x;
    this.y = y;
    return this;
  }

  /**
   * This method is used to create a x or y axis for the heatmap
   * @param  {string}  type                [axis type i.e. X or Y]
   * @param  {string} axisField           [Field that to be used for axis labels]
   * @param  {String}  [axisClass='label'] [css class(es) to apply to axis]
   * @return {object}                     [returns Heatmap]
   */
  createAxis(type, axisField, axisClass) {
    const { width, height } = this.chartDimensions;
    const svg = this.svg;
    const x = this.x;
    const y = this.y;

    if (type === 'x') {
      const axis = this.svg.select(axisClass);
      const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';
      let tickFormat;

      if (xAxisFormat.toLowerCase() === 'timestamp') {
        tickFormat = d3.timeFormat('%b %d %I:%M %p');
      } else {
        tickFormat = d3.timeFormat('%b %d');
      }

      const xAxis = g =>
        g
          .style('font-size', '18px')
          .attr('class', axisClass)
          .attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x).tickFormat(tickFormat));

      // Create the x axis gridlines
      const xAxisGridlines = g =>
        g
          .attr('class', 'x-axis grid')
          .attr('transform', `translate(0,${height})`)
          .call(d3.axisBottom(x).tickSize(-height).tickFormat(''));

      svg.append('g').call(xAxisGridlines);
      svg.append('g').call(xAxis);
    } else if (type === 'y') {
      const axis = this.svg.select(axisClass);

      const yAxis = g => g.style('font-size', '18px').attr('class', axisClass).call(d3.axisLeft(y));

      const yAxisGridlines = g => g.attr('class', 'y-axis grid').call(d3.axisLeft(y).tickSize(-width).tickFormat(''));

      svg.append('g').call(yAxisGridlines);
      svg.append('g').call(yAxis);
    }
    return this;
  } // createAxis

  /**
   * this method is used to create the graph series
   */
  createSeries() {
    const { width, height } = this.chartDimensions;
    const svg = this.svg;
    let data = this.data;
    const x = this.x;
    const y = this.y;
    const self = this;

    data = data.map(x => {
      let y = { ...x };
      if (y.rvalue === 'NaN') y.rvalue = null;
      if (y.min_value === 'NaN') y.min_value = null;
      if (y.max_value === 'NaN') y.max_value = null;
      return y;
    });

    const tempLine = this.createLine('rdate', 'rvalue', x, y);

    // Add an empty tooltip bar and line
    const tooltipBar = svg.append('line').attr('class', 'tooltip-bar');
    const tooltipLine = svg.append('line').attr('class', 'tooltip-line');
    const tooltipCard = d3
      .select('body')
      .append('div')
      .attr('class', `tooltip-card tooltip-card-${this.container.replace('#', '')}`);
    const contextMenu = d3.select('body').append('div').attr('class', 'context-menu');

    const ct2Series = this.appendSeries(svg, data, ['location_name', 'CT-2'], 'line', tempLine, this.color(0));
    const cc10Series = this.appendSeries(svg, data, ['location_name', 'CC-10'], 'line', tempLine, this.color(1));
    const cc7Series = this.appendSeries(svg, data, ['location_name', 'CC-7'], 'line', tempLine, this.color(2));
    const ct1Series = this.appendSeries(svg, data, ['location_name', 'CT-1'], 'line', tempLine, this.color(3));
    const ctp11Series = this.appendSeries(svg, data, ['location_name', 'CT-P1'], 'line', tempLine, this.color(4));
    const ctp12Series = this.appendSeries(svg, data, ['location_name', 'CT-P2'], 'line', tempLine, this.color(5));
    const pc1Series = this.appendSeries(svg, data, ['location_name', 'PC-1'], 'line', tempLine, this.color(6));
    const MHFD10091Series = this.appendSeries(
      svg,
      data,
      ['location_name', 'MHFD Cherry Ck'],
      'line',
      tempLine,
      this.color(7)
    );
    const NCDCUSW00093067Series = this.appendSeries(
      svg,
      data,
      ['location_name', 'NOAA KAPA'],
      'line',
      tempLine,
      this.color(8)
    );

    const NCDCUSW00093067MaxSeries = this.appendSeries(
      svg,
      data,
      ['location_name', 'NOAA KAPA Max'],
      'line',
      tempLine,
      this.color(8)
    );
    const NCDCUSW00093067MinSeries = this.appendSeries(
      svg,
      data,
      ['location_name', 'NOAA KAPA Min'],
      'line',
      tempLine,
      this.color(8)
    );

    const MHFD10091MaxSeries = this.appendSeries(
      svg,
      data,
      ['location_name', 'MHFD Cherry Ck Max'],
      'line',
      tempLine,
      this.color(7)
    );
    const MHFD10091MinSeries = this.appendSeries(
      svg,
      data,
      ['location_name', 'MHFD Cherry Ck Min'],
      'line',
      tempLine,
      this.color(7)
    );

    // Add an overlay to the chart so we can append the tooltip lines on hover
    const rect = svg
      .append('rect')
      .attr('transform', 'translate(0,0)')
      .attr('class', 'overlay')
      .style('opacity', 0)
      .attr('width', width)
      .attr('height', height)
      .on('mousemove', function () {
        self.createTooltip(this, x, tooltipBar, tooltipLine, tooltipCard);
      })
      .on('click', function () {
        if ((this.aggregationLevel = 'daily')) {
          self.createTable(this, x);
          // self.create15MinDisplay(x.invert(d3.mouse(this)[0]));
        }
      })
      .on('mouseout', function () {
        self.removeTooltip(tooltipCard);
      });

    return this;
  }

  /**
   * This utility method is used as a shorthand for appending a line or area series to the chart
   * @param  {object} svg      [svg to append to]
   * @param  {array} data      [data to plot]
   * @param  {string} classList [list of classes to assign the series]
   * @param  {function} series    [D3 line/area constructor to be used to plot the data]
   * @return {object}           [Returns DOM/SVG node]
   */
  appendSeries(svg, data, filter, classList, series, color) {
    const filteredData = data.filter(d => d[filter[0]] === filter[1]);
    return svg.append('path').datum(filteredData).attr('class', classList).attr('d', series).style('stroke', color);
  }

  /**
   * This utility method us used to create an D3 line series constructor
   * This method relies on the calcy() utility method to create a stacked line chart
   * @param  {array} xField [field to be used for x value ]
   * @param  {array} yField [field to be used for y value ]
   * @param  {object} x [x scale]
   * @param  {object} y [y scale]
   * @return {function}          [returns a D3 line constructor]
   */
  createLine(xField, yField, x, y) {
    const xAxisFormat = this.xAxis.format || 'mm/dd/yyyy';

    return d3
      .line()
      .curve(d3.curveBasis)
      .x(d => {
        if (xAxisFormat.toLowerCase() === 'timestamp') {
          return x(new Date(d[xField]));
        } else {
          return x(new Date(`${d[xField]} 00:00:00`));
        }
      })
      .y(d => y(d[yField]));
  }

  /**
   * This method is used to resize the SVG after the heatmap cards have rendered
   * @return {object} [returns Heatmap]
   */
  resize() {
    const svg = document.querySelector(`${this.container} svg g`);
    const container = document.querySelector(this.container);
    const height = svg.getBBox().height;
    container.style.height = `${height}px`;
    return this;
  }

  /**
   * This method is used to draw the heatmap
   * @return {object} [returns Heatmap]
   */
  draw() {
    this.validateConfig()
      .createSVG()
      .createScales()
      .createAxis('x', 'rdate', 'x-axis axis')
      .createAxis('y', 'rvalue', 'y-axis axis')
      .createSeries();
    return this;
  }
}
